import {SERIES} from "@/style/utils";

// Normalizzazione dei dati provider per lo scouting: club, nazionali, statura.

const normalizeKey = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[.'’]/g, "")
		.replace(/[-_/]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();

// Paese in italiano → nomi con cui può comparire negli export (italiano e inglese).
const COUNTRIES = {
	Albania: [],
	Algeria: [],
	Argentina: [],
	Australia: [],
	Austria: [],
	Belgio: ["Belgium"],
	Bosnia: [
		"Bosnia ed Erzegovina",
		"Bosnia and Herzegovina",
		"Bosnia-Herzegovina",
	],
	Brasile: ["Brazil", "Brasil"],
	Bulgaria: [],
	Camerun: ["Cameroon"],
	Canada: [],
	Cile: ["Chile"],
	Colombia: [],
	"Costa d'Avorio": ["Ivory Coast", "Côte d'Ivoire", "Cote d'Ivoire"],
	Croazia: ["Croatia"],
	Danimarca: ["Denmark"],
	Ecuador: [],
	Egitto: ["Egypt"],
	Estonia: [],
	Finlandia: ["Finland"],
	Francia: ["France"],
	Gambia: [],
	Georgia: [],
	Germania: ["Germany"],
	Ghana: [],
	Giappone: ["Japan"],
	Grecia: ["Greece"],
	Guinea: [],
	Inghilterra: ["England"],
	Irlanda: ["Ireland", "Republic of Ireland"],
	"Irlanda del Nord": ["Northern Ireland"],
	Islanda: ["Iceland"],
	Israele: ["Israel"],
	Italia: ["Italy"],
	Kosovo: [],
	Lettonia: ["Latvia"],
	Lituania: ["Lithuania"],
	Lussemburgo: ["Luxembourg"],
	Macedonia: ["Macedonia del Nord", "North Macedonia"],
	Mali: [],
	Malta: [],
	Marocco: ["Morocco"],
	Messico: ["Mexico"],
	Moldavia: ["Moldova"],
	Montenegro: [],
	Nigeria: [],
	Norvegia: ["Norway"],
	"Paesi Bassi": ["Olanda", "Netherlands", "Holland"],
	Paraguay: [],
	Perù: ["Peru"],
	Polonia: ["Poland"],
	Portogallo: ["Portugal"],
	"Repubblica Ceca": ["Czech Republic", "Czechia"],
	Romania: [],
	Russia: [],
	"San Marino": [],
	Scozia: ["Scotland"],
	Senegal: [],
	Serbia: [],
	Slovacchia: ["Slovakia"],
	Slovenia: [],
	Spagna: ["Spain"],
	"Stati Uniti": ["USA", "United States"],
	Svezia: ["Sweden"],
	Svizzera: ["Switzerland"],
	Tunisia: [],
	Turchia: ["Turkey", "Türkiye"],
	Ucraina: ["Ukraine"],
	Ungheria: ["Hungary"],
	Uruguay: [],
	Galles: ["Wales"],
};

const COUNTRY_BY_KEY = new Map(
	Object.entries(COUNTRIES).flatMap(([country, aliases]) =>
		[country, ...aliases].map((name) => [normalizeKey(name), country]),
	),
);

const AGE_GROUP = /\s(?:u|under)\s?(\d{2})$/;

// "Italia U19", "Italy U21", "Germany" → {country, ageGroup}; null se non è una nazionale.
export const parseNationalTeam = (name) => {
	let key = normalizeKey(name)
		.replace(/^(?:nazionale|national team)\s/, "")
		.replace(/\s(?:national team|nazionale)$/, "");
	const ageGroup = key.match(AGE_GROUP)?.[1];
	if (ageGroup) key = key.replace(AGE_GROUP, "");
	const country = COUNTRY_BY_KEY.get(key);
	return country ? {country, ageGroup: ageGroup ? "U" + ageGroup : null} : null;
};

// Forme societarie e parole che non identificano il club.
const CLUB_NOISE = new Set([
	"ac",
	"acd",
	"acf",
	"as",
	"asd",
	"bc",
	"calcio",
	"cf",
	"cfc",
	"fc",
	"spa",
	"srl",
	"ss",
	"ssc",
	"ssd",
	"uc",
	"us",
	"usd",
]);

const isYear = (key) => /^(?:18|19|20)\d{2}$/.test(key);

// Toglie dal fondo la categoria giovanile: U20, Under 19, Primavera, Next Gen, Youth.
const stripYouth = (tokens) => {
	const keys = tokens.map(normalizeKey);
	let end = tokens.length;
	for (;;) {
		const last = keys[end - 1],
			previous = keys[end - 2];
		if (/^u\d{2}$/.test(last) || ["primavera", "youth"].includes(last))
			end -= 1;
		else if (
			(previous === "under" && /^\d{2}$/.test(last)) ||
			(previous === "next" && last === "gen")
		)
			end -= 2;
		else break;
	}
	return tokens.slice(0, end);
};

// Nome normalizzato → nome del club.
export const CLUB_ALIASES = {
	internazionale: "Inter",
	"internazionale milano": "Inter",
	"inter milano": "Inter",
};

// "Hellas Verona F.C. U20" → "Hellas Verona"; "FC Internazionale Milano U16" → "Inter".
export const normalizeClubName = (name, aliases = CLUB_ALIASES) => {
	const original = String(name || "").trim();
	const tokens = stripYouth(original.split(/\s+/)).filter((token) => {
		const key = normalizeKey(token);
		return key && !CLUB_NOISE.has(key) && !isYear(key);
	});
	if (!tokens.length) return original;
	const club = tokens.join(" ");
	return aliases[normalizeKey(club)] || club;
};

// Statura dall'altezza in cm: L <174, M 174–184, H >184, VH >192.
export const STATURE_LABELS = {
	L: "Bassa",
	M: "Media",
	H: "Alta",
	VH: "Molto alta",
};

export const statureFromHeight = (height) => {
	const cm = parseFloat(height);
	if (!(cm > 0)) return null;
	if (cm < 174) return "L";
	if (cm <= 184) return "M";
	if (cm <= 192) return "H";
	return "VH";
};

// Posizione sul campo (x da sinistra, y dalla porta avversaria, in %) e reparto per ruolo:
// nomi dei ruoli del club, codici interni e, come ripiego, macro-aree. Il quinto valore è il
// codice per i ruoli esistenti accorpati a un altro (decisione del 2026-10-28, contratto 2.1).
const ROLE_POSITIONS = [
	[["Portiere", "POR"], 50, 92, "P"],
	[["Terzino destro", "TD"], 86, 72, "D"],
	[["Terzino sinistro", "TS"], 14, 72, "D"],
	[["Difensore centrale", "DC", "Difensore"], 50, 77, "D"],
	[["Quinto destro", "QD"], 88, 58, "D"],
	[["Quinto sinistro", "QS"], 12, 58, "D"],
	[["Mediano", "MED"], 50, 63, "C"],
	[["Regista", "REG"], 50, 56, "C"],
	[["Centrocampista centrale", "Centrocampista"], 50, 48, "C", "MEZ"],
	[["Mezzala", "MEZ"], 30, 47, "C"],
	[["Esterno destro", "ED"], 86, 40, "A"],
	[["Esterno sinistro", "ES"], 14, 40, "A"],
	[["Trequartista", "TRQ"], 50, 33, "C"],
	[["Ala destra"], 82, 22, "A", "ED"],
	[["Ala sinistra"], 18, 22, "A", "ES"],
	[["Seconda punta", "SP"], 38, 19, "A"],
	[["Punta centrale", "Prima punta", "PC", "Attaccante"], 50, 11, "A"],
];

const ROLE_BY_KEY = new Map(
	ROLE_POSITIONS.flatMap(([names, x, y, department, merged]) =>
		names.map((name) => [
			normalizeKey(name),
			{
				x,
				y,
				department,
				code: merged || names.find((n) => /^[A-Z]{2,3}$/.test(n)),
			},
		]),
	),
);

export const rolePosition = (role) => {
	const found = ROLE_BY_KEY.get(normalizeKey(role));
	return found ? {x: found.x, y: found.y} : null;
};

// Codice del ruolo (TD, MEZ, PC…); null per i ruoli sconosciuti.
export const roleCode = (role) =>
	ROLE_BY_KEY.get(normalizeKey(role))?.code || null;

export const DEPARTMENTS = [
	{id: "P", label: "Portieri"},
	{id: "D", label: "Difensori"},
	{id: "C", label: "Centrocampisti"},
	{id: "A", label: "Attaccanti"},
];

// Reparto del giocatore: portiere, poi ruolo specifico, poi macro-area.
export const playerDepartment = (player) => {
	const internal = player?.internalRole;
	if (DEPARTMENTS.some((d) => d.id === internal?.department))
		return internal.department;
	const found = ROLE_BY_KEY.get(normalizeKey(internal?.code || internal?.name));
	if (found) return found.department;
	if (player?.goalkeeper) return "P";
	for (const role of [player?.secondaryRole, player?.primaryRole]) {
		const found = ROLE_BY_KEY.get(normalizeKey(role));
		if (found) return found.department;
	}
	return null;
};

export const ATTITUDES = [
	{id: "D", label: "Difesa", color: SERIES[0]},
	{id: "R", label: "Regia", color: SERIES[2]},
	{id: "AR", label: "Rifinitura", color: SERIES[6]},
	{id: "AF", label: "Finalizzazione", color: SERIES[1]},
];
